import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Form,
  FormGroup,
  Label,
  FormFeedback,
  Button,
  Input,
  CustomInput,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import SyncSelect from "react-select";
import PropTypes from "prop-types";
import { useAxios } from "~/hooks/useAxios";
import {
  setURLFilter,
  setAllBidang,
  setShowPetaJalan,
  setTriggerRequest,
} from "~/redux/actions";
import {
  uppercaseFirstLetter,
  algorithmHierarki,
  HIERARKI_CASE_NAME,
  replaceAll,
  SELECT_LISTS_FILTERED_MAP,
  ucFirst,
} from "~/utils";
import request from "~/utils/request";

const FilteredPetaBasedBidang = () => {
  const { search } = useLocation();
  const { data } = useAxios(`/v1/sigtipe/?filter{nama.contains}=jalan`);
  const { handleSubmit, control, errors, setValue, register, watch } =
    useForm();
  const { sigTipeData, loadingRenderMap, isResetBidangHierarki } = useSelector(
    (state) => state.Map
  );

  const [petaJalanPick, setPetaJalanPick] = useState(null);

  const dispatch = useDispatch();
  const watchFilteredBasedBidang = watch("filter_based_bidang", null);
  const tipeQuerySearchParams = new URLSearchParams(search).get("tipe");

  const handleSearchPetaBasedBidang = (values) => {
    const isValues =
      replaceAll(
        values?.filter_based_bidang_lainnya?.toLowerCase(),
        " ",
        "_"
      ) ?? values?.filter_based_bidang?.value;

    let url = `/v1/sig/bidangbasedstream?tipe_id=${sigTipeData?.id}&value=${isValues}`;

    if (values?.filter_based_bidang?.value === "semua") {
      url = `/v1/sig/bidangbasedstream?tipe_id=${sigTipeData?.id}`;
    }

    dispatch(setURLFilter(url));
    dispatch(setShowPetaJalan(petaJalanPick));
  };

  const titleParams = uppercaseFirstLetter(
    replaceAll(tipeQuerySearchParams, "_", " ")
  );

  useEffect(() => {
    if (isResetBidangHierarki) {
      setValue("filter_based_bidang", null);
      dispatch(setURLFilter(""));
    }
  }, [isResetBidangHierarki]);

  return (
    <div className="filtered-maps">
      <Form
        autoComplete="off"
        onSubmit={handleSubmit(handleSearchPetaBasedBidang)}
      >
        <FormGroup>
          <Label>{titleParams}</Label>
          <Controller
            defaultValue={null}
            control={control}
            name="filter_based_bidang"
            rules={{ required: true }}
            render={({ onChange, value }) => (
              <SyncSelect
                value={value}
                onChange={(val) => onChange(val)}
                placeholder={`Pilih ${titleParams}`}
                options={[{ label: "Semua", value: "semua" }].concat(
                  SELECT_LISTS_FILTERED_MAP?.[tipeQuerySearchParams]
                )}
                styles={{
                  // Fixes the overlapping problem of the component
                  menu: (provided) => ({ ...provided, zIndex: 9999 }),
                }}
              />
            )}
          />

          {errors?.filter_based_bidang && (
            <FormFeedback className="d-block">
              Masukkan {titleParams}
            </FormFeedback>
          )}
        </FormGroup>

        {watchFilteredBasedBidang?.value === "lainnya" && (
          <FormGroup>
            <Label>{titleParams} Lainnya</Label>
            <Input
              name="filter_based_bidang_lainnya"
              innerRef={register({ required: true })}
            />

            {errors?.filter_based_bidang_lainnya && (
              <FormFeedback className="d-block">
                Masukkan {titleParams} Lainnya
              </FormFeedback>
            )}
          </FormGroup>
        )}

        {data?.map((el) => {
          const label = replaceAll(ucFirst(el?.nama), "_", " ");
          return (
            <FormGroup>
              <Controller
                name={el.nama}
                control={control}
                defaultValue={false}
                render={({ onChange }) => (
                  <CustomInput
                    type="switch"
                    id={el.nama}
                    name={el.nama}
                    label={`Tampilkan ${label}`}
                    onChange={(e) => {
                      setPetaJalanPick(el.nama);
                      onChange(e.target.checked);
                    }}
                    disabled={watch(
                      data?.find((elem) => elem.nama !== el.nama).nama
                    )}
                  />
                )}
              />
            </FormGroup>
          );
        })}

        <FormGroup>
          <Button
            block
            type="submit"
            color="primary"
            disabled={!sigTipeData || loadingRenderMap}
          >
            {loadingRenderMap ? "memuat peta..." : "  Lihat"}
          </Button>
        </FormGroup>
      </Form>
    </div>
  );
};

const FilteredPeta = ({ isBasedBidang }) => {
  const { desa } = useSelector((state) => state.Auth);
  const { allBidang, loadingRenderMap, isResetBidangHierarki } = useSelector(
    (state) => state.Map
  );

  const {
    control,
    watch,
    errors,
    setValue,
    handleSubmit,
    formState: { isValid },
  } = useForm({
    mode: "onChange",
  });

  const [hierarkiSig, setHierarkiSig] = useState(null);

  const history = useHistory();
  const dispatch = useDispatch();

  const watchSubDesa = watch("subdesa", null);
  const watchSubSubDesa = watch("subsubdesa", null);
  const watchRW = watch("rw", null);

  const subdesa = desa?.hierarki?.subdesa;
  const subsubdesa = desa?.hierarki?.subsubdesa;
  const RW = desa?.hierarki?.rw;
  const RT = desa?.hierarki?.rt;

  const getHierarkiName = algorithmHierarki(desa?.hierarki);

  const hirarkiRT = RW
    ? watchRW && watchRW?.value !== "semua" && RT
    : subsubdesa
    ? watchSubDesa && watchSubSubDesa?.value !== "semua"
      ? watchSubSubDesa
      : false
    : watchSubDesa;

  const hirarkiRW = RW
    ? subsubdesa
      ? watchSubSubDesa?.value !== "semua"
        ? watchSubSubDesa
        : false
      : watchSubDesa?.value !== "semua"
      ? watchSubDesa
      : false
    : RW;
  const hierarkiSubSubDesa = subsubdesa
    ? watchSubDesa?.value !== "semua"
      ? watchSubDesa
      : false
    : subsubdesa;

  const getDynamicHierarkiSig = (filter, target) => {
    let datas = ["semua"];

    if (filter) {
      request.get(`/v1/sighierarki?${filter}&target=${target}`).then((res) => {
        datas = [...datas, ...res.data.data];

        setHierarkiSig((prevHiearkiSig) => ({
          ...prevHiearkiSig,
          [target]: datas.map((hierarki) => ({
            label: uppercaseFirstLetter(hierarki),
            value: hierarki,
          })),
        }));
      });
    } else {
      request.get(`/v1/sighierarki?target=${target}`).then((res) => {
        datas = [...datas, ...res.data.data];

        setHierarkiSig((prevHiearkiSig) => ({
          ...prevHiearkiSig,
          [target]: datas.map((hierarki) => ({
            label: uppercaseFirstLetter(hierarki),
            value: hierarki,
          })),
        }));
      });
    }
  };

  const handleSearchBidang = (values) => {
    history.push("/peta-bidang");

    let urlFilter = "";

    if (values?.subdesa) {
      if (values?.subdesa?.value !== "semua") {
        urlFilter = `${urlFilter}&filter{subdesa}=${values?.subdesa?.value}`;
        if (allBidang) {
          dispatch(setAllBidang(false));
        }
      } else {
        dispatch(setAllBidang(true));
      }
    }

    if (values?.subsubdesa) {
      if (values?.subsubdesa?.value !== "semua") {
        urlFilter = `${urlFilter}&filter{subsubdesa}=${values?.subsubdesa?.value}`;
        if (allBidang) {
          dispatch(setAllBidang(false));
        }
      }
    }

    if (values?.rw) {
      if (values?.rw?.value !== "semua") {
        urlFilter = `${urlFilter}&filter{rw}=${values?.rw?.value}`;
        if (allBidang) {
          dispatch(setAllBidang(false));
        }
      }
    }

    if (values?.rt) {
      if (values?.rt?.value !== "semua") {
        urlFilter = `${urlFilter}&filter{rt}=${values?.rt?.value}`;
        if (allBidang) {
          dispatch(setAllBidang(false));
        }
      }
    }

    dispatch(setURLFilter(urlFilter));
    dispatch(setTriggerRequest(true));
  };
  const setAllValue = (name) =>
    setValue(name, { label: "Semua", value: "semua" });

  useEffect(() => {
    getDynamicHierarkiSig(null, "subdesa");
  }, []);

  useEffect(() => {
    const subDesaTosubSubDesa = [
      HIERARKI_CASE_NAME.ALL,
      HIERARKI_CASE_NAME.SUBDESA_SUBSUBDESA_RW,
      HIERARKI_CASE_NAME.SUBDESA_SUBSUBDESA_RT,
      HIERARKI_CASE_NAME.SUBDESA_SUBSUBDESA,
    ];

    const subDesaToRW = [
      HIERARKI_CASE_NAME.SUBDESA_RW_RT,
      HIERARKI_CASE_NAME.SUBDESA_RW,
    ];

    if (subDesaTosubSubDesa.includes(getHierarkiName)) {
      if (watchSubDesa) {
        setAllValue("subsubdesa");
        if (watchSubDesa?.value !== "semua") {
          getDynamicHierarkiSig(
            `filter{subdesa}=${watchSubDesa?.value ?? ""}`,
            "subsubdesa"
          );
        }
      }
    }

    if (subDesaToRW.includes(getHierarkiName)) {
      if (watchSubDesa) {
        setAllValue("rw");
        if (watchSubDesa?.value !== "semua") {
          getDynamicHierarkiSig(
            `filter{subdesa}=${watchSubDesa?.value ?? ""}`,
            "rw"
          );
        }
      }
    }

    if (getHierarkiName === HIERARKI_CASE_NAME.SUBDESA_RT) {
      if (watchSubDesa) {
        setAllValue("rt");
        if (watchSubDesa?.value !== "semua") {
          getDynamicHierarkiSig(
            `filter{subdesa}=${watchSubDesa?.value ?? ""}`,
            "rt"
          );
        }
      }
    }
  }, [watchSubDesa]);

  useEffect(() => {
    const subSubDesaToRW = [
      HIERARKI_CASE_NAME.ALL,
      HIERARKI_CASE_NAME.SUBDESA_SUBSUBDESA_RW,
    ];

    if (subSubDesaToRW.includes(getHierarkiName)) {
      if (watchSubSubDesa) {
        setAllValue("rw");

        if (watchSubSubDesa?.value !== "semua") {
          getDynamicHierarkiSig(
            `filter{subdesa}=${watchSubDesa?.value ?? ""}&filter{subsubdesa}=${
              watchSubSubDesa?.value ?? ""
            }`,
            "rw"
          );
        }
      }
    }

    if (getHierarkiName === HIERARKI_CASE_NAME.SUBDESA_SUBSUBDESA_RT) {
      if (watchSubSubDesa) {
        setAllValue("rt");

        if (watchSubSubDesa?.value !== "semua") {
          getDynamicHierarkiSig(
            `filter{subdesa}=${watchSubDesa?.value ?? ""}&filter{subsubdesa}=${
              watchSubSubDesa?.value ?? ""
            }`,
            "rt"
          );
        }
      }
    }
  }, [watchSubSubDesa]);

  useEffect(() => {
    const rwToRt = [HIERARKI_CASE_NAME.ALL, HIERARKI_CASE_NAME.SUBDESA_RW_RT];

    if (rwToRt.includes(getHierarkiName)) {
      setAllValue("rt");

      if (watchRW?.value !== "semua") {
        getDynamicHierarkiSig(
          `filter{subdesa}=${watchSubDesa?.value ?? ""}&filter{subsubdesa}=${
            watchSubSubDesa?.value ?? ""
          }&filter{rw}=${watchRW?.value ?? ""}`,
          "rt"
        );
      }
    }

    if (getHierarkiName === HIERARKI_CASE_NAME.SUBDESA_RW_RT) {
      if (watchRW) {
        setAllValue("rt");

        if (watchRW?.value !== "semua") {
          getDynamicHierarkiSig(
            `filter{subdesa}=${watchSubDesa?.value ?? ""}&filter{rw}=${
              watchRW?.value ?? ""
            }`,
            "rt"
          );
        }
      }
    }
  }, [watchRW]);

  useEffect(() => {
    if (isResetBidangHierarki) {
      setValue("subdesa", null);
      setValue("subsubdesa", null);
      setValue("rw", null);
      setValue("rt", null);
      dispatch(setURLFilter(""));
    }
  }, [isResetBidangHierarki]);

  const keySelectSubSubDesa = !!watchSubDesa?.value
    ? watchSubDesa?.value
    : null;

  const keySelectRW = subsubdesa
    ? !!watchSubSubDesa?.value
      ? watchSubSubDesa?.value
      : null
    : !!watchSubDesa?.value
    ? watchSubDesa?.value
    : null;

  const keySelectRT = RW
    ? !!watchRW?.value
      ? watchRW?.value
      : null
    : subsubdesa
    ? !!watchSubSubDesa?.value
      ? watchSubSubDesa?.value
      : watchSubDesa?.value
    : null;

  if (isBasedBidang) {
    return <FilteredPetaBasedBidang />;
  }

  return (
    <div className="filtered-maps">
      <Form onSubmit={handleSubmit(handleSearchBidang)}>
        {subdesa && (
          <FormGroup>
            <Label>{uppercaseFirstLetter(desa?.hierarki?.jenis_subdesa)}</Label>
            <Controller
              name="subdesa"
              defaultValue={null}
              control={control}
              rules={{ required: desa?.hierarki?.subdesa }}
              render={({ onChange, value }) => (
                <SyncSelect
                  value={value}
                  options={hierarkiSig?.subdesa}
                  onChange={(val) => onChange(val)}
                  placeholder={`Pilih ${uppercaseFirstLetter(
                    desa?.hierarki?.jenis_subdesa
                  )}`}
                  styles={{
                    // Fixes the overlapping problem of the component
                    menu: (provided) => ({ ...provided, zIndex: 9999 }),
                  }}
                />
              )}
            />

            {errors?.subdesa && (
              <FormFeedback className="d-block">
                Masukkan {desa?.hierarki?.jenis_subdesa}
              </FormFeedback>
            )}
          </FormGroup>
        )}

        {hierarkiSubSubDesa && (
          <FormGroup>
            <Label>
              {uppercaseFirstLetter(desa?.hierarki?.jenis_subsubdesa)}
            </Label>
            <Controller
              name="subsubdesa"
              defaultValue={null}
              control={control}
              rules={{ required: !desa?.hierarki?.subdesa }}
              key={keySelectSubSubDesa}
              render={({ onChange, value }) => (
                <SyncSelect
                  value={value}
                  placeholder={`Pilih ${uppercaseFirstLetter(
                    desa?.hierarki?.jenis_subsubdesa
                  )}`}
                  options={hierarkiSig?.subsubdesa}
                  onChange={(val) => onChange(val)}
                  styles={{
                    // Fixes the overlapping problem of the component
                    menu: (provided) => ({ ...provided, zIndex: 9999 }),
                  }}
                />
              )}
            />

            {errors?.subsubdesa && (
              <FormFeedback className="d-block">
                Masukkan {desa?.hierarki?.jenis_subsubdesa}
              </FormFeedback>
            )}
          </FormGroup>
        )}

        {hirarkiRW && (
          <FormGroup className="position-relative mb-2">
            <Label>RW</Label>
            <Controller
              name="rw"
              defaultValue={null}
              key={keySelectRW}
              control={control}
              render={({ onChange, value }) => (
                <SyncSelect
                  value={value}
                  options={hierarkiSig?.rw}
                  placeholder="Pilih RW"
                  onChange={(val) => onChange(val)}
                  styles={{
                    // Fixes the overlapping problem of the component
                    menu: (provided) => ({ ...provided, zIndex: 9999 }),
                  }}
                />
              )}
            />
          </FormGroup>
        )}

        {hirarkiRT && (
          <FormGroup className="position-relative mb-2">
            <Label>RT</Label>
            <Controller
              name="rt"
              control={control}
              key={keySelectRT}
              defaultValue={null}
              render={({ onChange, value }) => (
                <SyncSelect
                  value={value}
                  options={hierarkiSig?.rt}
                  placeholder="Pilih RT"
                  onChange={(val) => onChange(val)}
                  styles={{
                    // Fixes the overlapping problem of the component
                    menu: (provided) => ({ ...provided, zIndex: 9999 }),
                  }}
                />
              )}
            />
          </FormGroup>
        )}

        <FormGroup>
          <Button
            type="submit"
            color="primary"
            disabled={!isValid || loadingRenderMap}
            block
          >
            {loadingRenderMap ? "memuat peta..." : "  Lihat"}
          </Button>
        </FormGroup>
      </Form>
    </div>
  );
};

FilteredPeta.propTypes = {
  isBasedBidang: PropTypes.bool,
};

export default FilteredPeta;
